<div
  *ngIf="show"
  class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-xs p-4 overflow-y-auto"
>
  <div
    class="bg-white rounded-2xl w-full max-w-xl shadow-2xl border border-gray-100 flex flex-col max-h-[92vh] overflow-hidden my-4"
  >
    <!-- Modal Header -->
    <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/60">
      <div class="flex items-center space-x-3">
        <div class="p-2 rounded-lg bg-indigo-50 text-indigo-600 shrink-0">
          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
        </div>
        <div>
          <div class="flex items-center gap-2">
            <h2 class="text-base font-semibold text-gray-900">Return Transaction</h2>
            <span
              *ngIf="transaction.payment_method"
              class="text-[11px] px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-700"
            >
              {{ transaction.payment_method }}
            </span>
          </div>
          <p class="text-xs text-gray-500 mt-0.5">
            Process return for transaction {{ transaction.uuid | slice:0:8 }}…
          </p>
        </div>
      </div>

      <button
        type="button"
        (click)="close.emit()"
        class="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition cursor-pointer"
        title="Close"
      >
        <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Modal Body -->
    <div class="p-6 overflow-y-auto space-y-5 text-sm text-gray-800">

      <!-- ========================================== -->
      <!-- 1. Transaction details                     -->
      <!-- ========================================== -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500">
            1. Transaction Details
          </h3>
          <span class="text-[11px] text-gray-400">Recap</span>
        </div>

        <div class="bg-gray-50/70 p-4 rounded-xl border border-gray-200/80 text-xs text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <!-- ID -->
          <div>
            <span class="text-gray-500 block text-[11px] mb-0.5">Transaction ID</span>
            <div class="flex items-center gap-1.5 font-mono text-gray-900">
              <span class="truncate max-w-[190px]" [title]="transaction.uuid">{{ transaction.uuid }}</span>
              <button
                type="button"
                (click)="copyToClipboard(transaction.uuid || '', 'uuid')"
                class="text-gray-400 hover:text-indigo-600 cursor-pointer"
                [title]="copiedField === 'uuid' ? 'Copied!' : 'Copy ID'"
              >
                <svg *ngIf="copiedField !== 'uuid'" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span *ngIf="copiedField === 'uuid'" class="text-[10px] text-emerald-600 font-bold">✓</span>
              </button>
            </div>
          </div>

          <!-- Date -->
          <div>
            <span class="text-gray-500 block text-[11px] mb-0.5">Date</span>
            <span class="text-gray-900">{{ transaction.created_at | date:'dd/MM/yyyy HH:mm' }}</span>
          </div>

          <!-- Sender -->
          <div *ngIf="hasSenderDetails" class="sm:col-span-2">
            <span class="text-gray-500 block text-[11px] mb-0.5">Sender</span>
            <span class="text-gray-900 font-medium">
              {{ senderDetails.name || 'Unknown Sender' }}
              <span *ngIf="senderDetails.iban || senderDetails.account_number || senderDetails.crypto_address" class="text-gray-500 font-normal">
                ({{ senderDetails.iban || senderDetails.account_number || (senderDetails.crypto_address | slice:0:16) + '...' }})
              </span>
            </span>
          </div>

          <!-- Original Amount -->
          <div>
            <span class="text-gray-500 block text-[11px] mb-0.5">Original Amount</span>
            <div class="font-semibold text-gray-900">
              <span>{{ transaction.amount }} {{ transaction.amount_currency || 'USD' }}</span>
              <span *ngIf="transaction.equivalent_in_euros" class="text-gray-500 font-normal ml-1">
                (≈ {{ transaction.equivalent_in_euros }} €)
              </span>
            </div>
          </div>

          <!-- Fees & Accounting -->
          <div>
            <span class="text-gray-500 block text-[11px] mb-0.5">Fees & Accounting</span>
            <div class="text-gray-900">
              <span>{{ transaction.fees || '0.00' }} {{ transaction.fees_currency || transaction.amount_currency || 'USD' }}</span>
              <span class="text-gray-300 mx-1.5">•</span>
              <span
                class="inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-semibold"
                [ngClass]="{
                  'bg-emerald-50 text-emerald-700 border border-emerald-200': transaction.accounting_type === 'CREDIT',
                  'bg-red-50 text-red-700 border border-red-200': transaction.accounting_type === 'DEBIT'
                }"
              >
                {{ transaction.accounting_type }}
              </span>
            </div>
          </div>

          <!-- Type & Status -->
          <div class="sm:col-span-2 flex items-center gap-2 pt-1 border-t border-gray-200/60">
            <span class="text-gray-500 text-[11px]">Type:</span>
            <span class="font-medium text-gray-800">{{ transaction.transaction_type }}</span>
            <span class="text-gray-300">•</span>
            <span class="text-gray-500 text-[11px]">Status:</span>
            <span
              class="px-2 py-0.5 rounded text-[10px] font-medium"
              [ngClass]="{
                'bg-emerald-100/70 text-emerald-800': transaction.status === 'SUCCEEDED',
                'bg-amber-100/70 text-amber-800': transaction.status === 'PROCESSING',
                'bg-rose-100/70 text-rose-800': transaction.status === 'FAILED',
                'bg-gray-100 text-gray-700': !['SUCCEEDED', 'PROCESSING', 'FAILED'].includes(transaction.status || '')
              }"
            >
              {{ transaction.status }}
            </span>
          </div>
        </div>
      </div>

      <!-- ========================================== -->
      <!-- 2. Recipient                               -->
      <!-- ========================================== -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500">
            2. Recipient
          </h3>

          <button
            *ngIf="recipientMode === 'original_sender'"
            type="button"
            (click)="toggleSelectAnotherRecipient()"
            class="text-xs text-indigo-600 hover:text-indigo-800 font-medium hover:underline inline-flex items-center gap-1 cursor-pointer"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Another recipient
          </button>

          <button
            *ngIf="recipientMode === 'another_recipient'"
            type="button"
            (click)="useOriginalSender()"
            class="text-xs text-gray-500 hover:text-gray-700 underline inline-flex items-center gap-1 cursor-pointer"
          >
            ← Use original sender
          </button>
        </div>

        <!-- Mode A: Original Sender -->
        <div *ngIf="recipientMode === 'original_sender'" class="bg-white p-4 rounded-xl border border-gray-200/80 shadow-xs text-xs space-y-2">
          <div class="flex items-center justify-between pb-2 border-b border-gray-100">
            <span class="font-medium text-gray-700">Transaction Sender</span>
            <span class="text-[10px] uppercase font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
              {{ initialTransactionType }} • Original Sender
            </span>
          </div>

          <div *ngIf="hasSenderDetails; else noSenderDetails" class="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1 text-gray-700">
            <div *ngIf="senderDetails.name">
              <span class="text-gray-400 block text-[11px]">Name</span>
              <span class="font-medium text-gray-900">{{ senderDetails.name }}</span>
            </div>

            <div *ngIf="senderDetails.bank_name && !isCryptoTransaction">
              <span class="text-gray-400 block text-[11px]">Bank</span>
              <span class="text-gray-900">{{ senderDetails.bank_name }}</span>
            </div>

            <!-- SEPA Specific -->
            <div *ngIf="senderDetails.iban">
              <span class="text-gray-400 block text-[11px]">IBAN</span>
              <div class="flex items-center gap-1.5 font-mono text-gray-900">
                <span>{{ senderDetails.iban }}</span>
                <button
                  type="button"
                  (click)="copyToClipboard(senderDetails.iban, 'iban')"
                  class="text-gray-400 hover:text-indigo-600 cursor-pointer"
                  [title]="copiedField === 'iban' ? 'Copied!' : 'Copy IBAN'"
                >
                  <svg *ngIf="copiedField !== 'iban'" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span *ngIf="copiedField === 'iban'" class="text-[10px] text-emerald-600 font-bold">✓</span>
                </button>
              </div>
            </div>

            <div *ngIf="senderDetails.bic">
              <span class="text-gray-400 block text-[11px]">BIC</span>
              <span class="font-mono text-gray-900">{{ senderDetails.bic }}</span>
            </div>

            <!-- Postal Address (Bank Transfer / SEPA / ACH) -->
            <div *ngIf="!isCryptoTransaction && senderDetails.address" class="sm:col-span-2">
              <span class="text-gray-400 block text-[11px]">Address</span>
              <span class="text-gray-900">{{ senderDetails.address }}</span>
            </div>

            <!-- Crypto Specific -->
            <div *ngIf="isCryptoTransaction && senderDetails.crypto_address" class="sm:col-span-2">
              <span class="text-gray-400 block text-[11px]">Crypto Address</span>
              <div class="flex items-center gap-1.5 font-mono text-gray-900">
                <span class="break-all">{{ senderDetails.crypto_address }}</span>
                <button
                  type="button"
                  (click)="copyToClipboard(senderDetails.crypto_address, 'crypto')"
                  class="text-gray-400 hover:text-indigo-600 shrink-0 cursor-pointer"
                  [title]="copiedField === 'crypto' ? 'Copied!' : 'Copy Address'"
                >
                  <svg *ngIf="copiedField !== 'crypto'" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span *ngIf="copiedField === 'crypto'" class="text-[10px] text-emerald-600 font-bold">✓</span>
                </button>
              </div>
            </div>

            <!-- ACH Specific -->
            <div *ngIf="senderDetails.routing_number">
              <span class="text-gray-400 block text-[11px]">Routing Number (ACH)</span>
              <span class="font-mono text-gray-900">{{ senderDetails.routing_number }}</span>
            </div>

            <div *ngIf="senderDetails.account_number">
              <span class="text-gray-400 block text-[11px]">Account Number (ACH)</span>
              <span class="font-mono text-gray-900">{{ senderDetails.account_number }}</span>
            </div>
          </div>

          <ng-template #noSenderDetails>
            <div class="py-2 text-gray-500 italic text-[11px]">
              Original sender details are not available on this transaction.
              <button
                type="button"
                (click)="toggleSelectAnotherRecipient()"
                class="text-indigo-600 hover:text-indigo-800 underline ml-1 not-italic font-medium cursor-pointer"
              >
                Choose another recipient
              </button>
            </div>
          </ng-template>
        </div>

        <!-- Mode B: Another Recipient Selection (Locked to initial transaction type) -->
        <div *ngIf="recipientMode === 'another_recipient'" class="bg-white p-4 rounded-xl border border-gray-200/80 shadow-xs space-y-3">
          <div class="flex items-center justify-between pb-2 border-b border-gray-100">
            <span class="text-xs font-medium text-gray-700">Recipient Destination</span>
            <span class="text-[10px] uppercase font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded">
              Locked to {{ initialTransactionType }}
            </span>
          </div>

          <!-- Loading State -->
          <div *ngIf="loadingRecipients" class="py-3 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
            <div class="w-3.5 h-3.5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <span>Loading {{ recipientTypeLabel | lowercase }}s…</span>
          </div>

          <!-- Bank / SEPA / ACH Selection -->
          <div *ngIf="!loadingRecipients && !isCryptoTransaction">
            <label class="block text-xs font-medium text-gray-700 mb-1">
              Select Existing {{ recipientTypeLabel }}
            </label>
            <select
              [(ngModel)]="selectedRecipientId"
              class="w-full border border-gray-300 rounded-lg p-2 text-xs bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            >
              <option value="">-- Choose a {{ isAchTransaction ? 'ACH' : 'SEPA' }} recipient --</option>
              <option *ngFor="let rec of bankRecipients" [value]="rec.uuid">
                {{ rec.first_name }} {{ rec.last_name }} ({{ rec.iban }})
              </option>
            </select>
            <p *ngIf="bankRecipients.length === 0" class="text-[11px] text-gray-500 mt-1">
              No existing {{ isAchTransaction ? 'ACH' : 'SEPA' }} recipients found for this user.
            </p>
          </div>

          <!-- Crypto Selection -->
          <div *ngIf="!loadingRecipients && isCryptoTransaction">
            <label class="block text-xs font-medium text-gray-700 mb-1">
              Select Existing Crypto Recipient
            </label>
            <select
              [(ngModel)]="selectedRecipientId"
              class="w-full border border-gray-300 rounded-lg p-2 text-xs bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            >
              <option value="">-- Choose a crypto recipient --</option>
              <option *ngFor="let rec of cryptoRecipients" [value]="rec.uuid">
                {{ rec.name || 'Wallet' }} ({{ rec.address }})
              </option>
            </select>
            <p *ngIf="cryptoRecipients.length === 0" class="text-[11px] text-gray-500 mt-1">
              No crypto recipients found for this user.
            </p>
          </div>

          <!-- Selected Recipient Preview Card -->
          <div
            *ngIf="selectedRecipientId && getSelectedRecipientObj() as sel"
            class="bg-gray-50 p-3 rounded-lg border border-gray-200 text-xs text-gray-700 space-y-1"
          >
            <div class="flex items-center justify-between text-gray-900 font-semibold mb-1">
              <span>Selected Recipient</span>
              <span class="text-[10px] uppercase font-bold text-indigo-600 bg-white px-2 py-0.5 rounded border border-indigo-100">
                {{ initialTransactionType }}
              </span>
            </div>
            <div *ngIf="!isCryptoTransaction">
              <div><span class="text-gray-400">Name:</span> <span class="font-medium text-gray-900 ml-1">{{ sel.first_name }} {{ sel.last_name }}</span></div>
              <div><span class="text-gray-400">IBAN:</span> <span class="font-mono text-gray-900 ml-1">{{ sel.iban }}</span></div>
              <div *ngIf="sel.bic"><span class="text-gray-400">BIC:</span> <span class="font-mono text-gray-900 ml-1">{{ sel.bic }}</span></div>
            </div>
            <div *ngIf="isCryptoTransaction">
              <div><span class="text-gray-400">Name:</span> <span class="font-medium text-gray-900 ml-1">{{ sel.name }}</span></div>
              <div><span class="text-gray-400">Address:</span> <span class="font-mono text-[11px] text-gray-900 ml-1 break-all">{{ sel.address }}</span></div>
            </div>
          </div>
        </div>
      </div>

      <!-- ========================================== -->
      <!-- 3. Amount, Currency & Balances             -->
      <!-- ========================================== -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500">
            3. Amount
          </h3>
          <span class="text-[11px] text-gray-400">USD, EUR</span>
        </div>

        <div class="space-y-4">
          <!-- Currency & Amount Row -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <!-- Currency (Locked) -->
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">
                Currency
              </label>
              <div class="flex items-center h-10 px-3 bg-gray-100 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 select-none">
                <span class="font-semibold text-gray-900">{{ selectedCurrency }}</span>
                <span class="ml-auto inline-flex items-center gap-1 text-[11px] text-gray-400">
                  <svg class="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Locked
                </span>
              </div>
              <p class="text-[11px] text-gray-400 mt-1">
                Locked to initial transaction currency.
              </p>
            </div>

            <!-- Amount -->
            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="block text-xs font-medium text-gray-700">
                  Amount ({{ selectedCurrency }})
                </label>
                <button
                  type="button"
                  (click)="setMaxAmount()"
                  class="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium hover:underline cursor-pointer"
                >
                  Max: {{ getMaxReturnAmount().toFixed(2) }}
                </button>
              </div>

              <div class="relative">
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  [(ngModel)]="returnAmount"
                  (input)="validateAmount()"
                  placeholder="Enter amount"
                  class="w-full h-10 border rounded-lg px-3 pr-12 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 placeholder:text-gray-400 placeholder:opacity-40"
                  [ngClass]="{
                    'border-red-400 focus:ring-red-400/20 focus:border-red-500': isInvalidAmount,
                    'border-gray-300': !isInvalidAmount
                  }"
                />
                <span class="absolute right-3 top-2.5 text-xs font-medium text-gray-400 pointer-events-none">
                  {{ selectedCurrency }}
                </span>
              </div>
              <p *ngIf="isInvalidAmount" class="text-[11px] text-red-600 mt-1">
                {{ amountErrorMessage }}
              </p>
            </div>
          </div>

          <!-- Note -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">
              Note
            </label>
            <input
              type="text"
              [(ngModel)]="note"
              placeholder="e.g. Moneco - Return"
              class="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 placeholder:text-gray-400 placeholder:opacity-40"
            />
          </div>

          <!-- User Balances -->
          <div class="pt-2 border-t border-gray-100">
            <!-- Balances Bar -->
            <div class="flex flex-wrap items-center justify-between text-xs text-gray-600 bg-gray-50/80 p-2.5 rounded-lg border border-gray-200/70 gap-2">
              <span class="font-medium text-gray-700">User Balances:</span>
              <div class="flex items-center gap-3">
                <span>
                  <span class="text-gray-400">EUR:</span>
                  <span class="font-semibold text-gray-900 ml-1">{{ getBalanceForCurrency('EUR').toFixed(2) }} €</span>
                </span>
                <span class="text-gray-300">|</span>
                <span>
                  <span class="text-gray-400">USD:</span>
                  <span class="font-semibold text-gray-900 ml-1">${{ getBalanceForCurrency('USD').toFixed(2) }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Modal Footer Actions -->
    <div class="px-6 py-3.5 bg-gray-50/70 border-t border-gray-100 flex items-center justify-end space-x-2">
      <button
        type="button"
        (click)="close.emit()"
        class="px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold transition cursor-pointer shadow-xs"
      >
        Cancel
      </button>
      <button
        type="button"
        (click)="submit()"
        [disabled]="isInvalidAmount || !returnAmount || returnAmount <= 0"
        class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition cursor-pointer shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Confirm Return
      </button>
    </div>
  </div>
</div>
